import React from 'react';
import { TreeItem } from '@mui/x-tree-view';
import { CircularProgress } from '@mui/material';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import ScheduleIcon from '../components/ScheduleIcon';
import ScheduleOutlinedIcon from '../components/ScheduleOutlinedIcon';
import type { Project } from '../services/folder.service';
import { ITestCase } from '../types/interfaces/testCase.interface';
import { buildNodeKey } from '../utils/nodeId';
import { TestCaseTreeItem } from './TestCaseTreeItem'; // Import the new component
import TreeItemLabel from './TreeItemLabel';
import { formatTimestamp } from '../helpers/date.helper';

// Replicate TestCaseLoadingState interface (or import if shared)
interface TestCaseLoadingState {
  loading: boolean;
  refreshing?: boolean; // Background refresh while keeping existing data visible
  data: ITestCase[] | null;
  error?: string | null;
}

interface ProjectTreeItemProps {
  project: Project;
  parentFolderId?: number; // Optional: used for context menu prop
  cutProjectIds: number[];
  loadedTestCases: Record<number, TestCaseLoadingState>;
  onContextMenu: (
    event: React.MouseEvent,
    nodeId: string,
    nodeType: 'project' | 'testcase', // Allow 'testcase' here for context menu prop drilling
    parentId?: number
  ) => void;
  // Add selection change callback
  onSelectedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[] | string) => void;
  // No need to pass loadProjectTestCases here, expansion is handled by FolderTree
  filterText: string;
  ancestorMatched?: boolean;
  showModifiedDates: boolean;
}

export const ProjectTreeItem: React.FC<ProjectTreeItemProps> = ({
  project,
  parentFolderId,
  cutProjectIds,
  loadedTestCases,
  onContextMenu,
  onSelectedItemsChange, // Destructure new prop
  filterText,
  ancestorMatched = false,
  showModifiedDates,
}) => {
  const itemId = buildNodeKey({ type: 'project', id: project.testProjectID });
  const isCut = cutProjectIds.includes(project.testProjectID);
  const projectState = loadedTestCases[project.testProjectID];
  const label = `${project.testProjectID} - ${project.name}`;
  const subText =
    showModifiedDates && project.lastModifiedTime
      ? formatTimestamp(project.lastModifiedTime)
      : undefined;
  
  const testCaseCount = showModifiedDates && projectState?.data ? projectState.data.length : undefined;

  // Determine icon color based on schedule status
  const getIconColor = () => {
    if (!project.hasSchedule) return undefined;
    return project.hasEnabledSchedule ? '#1F9997' : undefined; // Bright teal for enabled, default grey for disabled
  };

  const searchTerm = filterText.trim().toLowerCase();
  const projectMatch = searchTerm
    ? project.name.toLowerCase().includes(searchTerm) ||
      String(project.testProjectID).includes(searchTerm)
    : false;
  const keepAllTcs = ancestorMatched || projectMatch;
  const filteredTestCases = React.useMemo(() => {
    if (!projectState || !projectState.data) return [] as ITestCase[];
    if (!searchTerm || keepAllTcs) return projectState.data;
    return projectState.data.filter(
      (tc) =>
        tc.name?.toLowerCase().includes(searchTerm) ||
        String(tc.testCaseId).includes(searchTerm)
    );
  }, [projectState, searchTerm, keepAllTcs]);

  return (
    <TreeItem
      key={itemId}
      itemId={itemId}
      label={<TreeItemLabel text={label} subText={subText} searchTerm={filterText} testCaseCount={testCaseCount} />}
      slots={{
        collapseIcon: project.hasSchedule ? ScheduleOutlinedIcon : LibraryBooksOutlinedIcon,
        expandIcon: project.hasSchedule ? ScheduleIcon : LibraryBooksIcon,
        endIcon: project.hasSchedule ? ScheduleIcon : LibraryBooksIcon,
      }}
      slotProps={{
        collapseIcon: { 
          fontSize: 'small',
          style: { color: getIconColor() }
        },
        expandIcon: { 
          fontSize: 'small',
          style: { color: getIconColor() }
        },
        endIcon: { 
          fontSize: 'small', 
          color: project.hasSchedule ? undefined : 'action',
          style: project.hasSchedule ? { color: getIconColor() } : undefined
        },
      }}
      onContextMenu={(e) => onContextMenu(e, itemId, 'project', parentFolderId)}
      sx={isCut ? { opacity: 0.5 } : undefined}
    >
      {/* Render children based on loading state */}
      {!projectState ? (
        // If never expanded, render a dummy item to allow expansion icon
        <TreeItem itemId={`project-dummy-${project.testProjectID}-${parentFolderId ?? 'unsorted'}`} sx={{ display: 'none' }} />
      ) : projectState.loading ? (
        <TreeItem itemId={`project-loading-${project.testProjectID}`} label="Loading..." slots={{ icon: () => <CircularProgress size={16} sx={{ ml: 1 }} /> }} disabled />
      ) : projectState.error ? (
        <TreeItem itemId={`project-error-${project.testProjectID}`} label={projectState.error || 'Error'} sx={{ color: 'error.main' }} disabled />
      ) : filteredTestCases && filteredTestCases.length > 0 ? (
        // Render actual test cases using TestCaseTreeItem
        filteredTestCases.map((tc) => (
          <TestCaseTreeItem
            key={buildNodeKey({ type: 'testcase', id: tc.testCaseId })} // Use unique key
            testCase={tc}
            parentProjectId={project.testProjectID}
            onContextMenu={onContextMenu as any} // Pass context menu handler down (adjust type if needed)
            onSelectedItemsChange={onSelectedItemsChange} // Pass callback down
            showModifiedDates={showModifiedDates}
            filterText={filterText}
          />
        ))
      ) : (
        <TreeItem itemId={`project-empty-${project.testProjectID}`} label="No test cases" disabled />
      )}
    </TreeItem>
  );
}; 